import Papa from 'papaparse';
import { PALETTE, GOALS, DELIVERY_TYPES, DAYS, TIME_OF_DAY_BUCKETS } from './constants';
import { uid, sanitize } from './utils';
import { supportsBlend, isBlend, parseBlendText, blendConc } from './blend';

const DELIVERY_TYPE_MAP = Object.fromEntries(DELIVERY_TYPES.map(d => [d.label.toLowerCase(), d.id]));
const GOAL_MAP = Object.fromEntries(Object.entries(GOALS).map(([key, g]) => [g.label.toLowerCase(), key]));
const STATUS_MAP = { 'new — unopened': 'new', 'open — in use': 'open', 'empty — depleted': 'empty' };
const DAY_MAP = Object.fromEntries(DAYS.map(d => [d.toLowerCase(), d]));

const STACK_ITEMS_HEADERS = ['Full Name', 'Short Name', 'Delivery Type', 'Goal', 'Dose Days', 'Time of Day', 'Blend Ingredients', 'Default BAC (mL)'];
const INJECTABLE_VIALS_HEADERS = ['Stack Item Name', 'Vial Label', 'Concentration (mg/mL)', 'Total Volume (mL)', 'Pharmacy', 'Lot #', 'Received Date', 'BUD / Expiration', 'Status', 'Cost ($)', 'Blend Ingredients'];
const PILL_BOTTLES_HEADERS = ['Stack Item Name', 'Bottle/Pen Label', 'Pill/Dose Count', 'Dose per Pill/Pen (mg)', 'Pills per Dose', 'Pharmacy', 'Lot #', 'Received Date', 'BUD / Expiration', 'Status', 'Cost ($)'];

// Parses CSV text into row objects keyed by the *expected* header names (case-insensitive
// match against the actual file's headers), so downstream code never has to worry about
// case or column order. A column present in expectedHeaders but absent from the file
// resolves to undefined for every row (treated as blank, not a parse failure) — required-ness
// is enforced later, per-row, by the caller.
function parseWithHeaders(csvText, expectedHeaders) {
  const { data, meta } = Papa.parse(csvText.trim(), { header: true, skipEmptyLines: true });
  const actualByNormalized = Object.fromEntries((meta.fields || []).map(f => [f.trim().toLowerCase(), f]));
  const headerKeyFor = expected => actualByNormalized[expected.toLowerCase()];
  return data.map((rawRow, idx) => {
    const row = {};
    for (const expected of expectedHeaders) {
      const actualKey = headerKeyFor(expected);
      row[expected] = actualKey ? (rawRow[actualKey] || '').trim() : '';
    }
    return { row, sheetRow: idx + 2 };
  });
}

function resolveGoal(value) {
  if (!value) return 'energy';
  return GOAL_MAP[value.trim().toLowerCase()] || null;
}

function resolveDeliveryType(value) {
  return DELIVERY_TYPE_MAP[(value || '').trim().toLowerCase()] || null;
}

// Accepts the long labels ("Open — In Use") and the template dropdown's short ones ("Open") —
// only the first word decides.
function resolveStatus(value) {
  if (!value) return 'new';
  const v = value.trim().toLowerCase();
  if (STATUS_MAP[v]) return STATUS_MAP[v];
  const first = v.split(/[\s—–-]+/)[0];
  return ['new', 'open', 'empty'].includes(first) ? first : null;
}

// Google Sheets' CSV download writes cells as displayed ("$1,250.00").
const parseMoney = value => parseFloat(String(value || '').replace(/[$,\s]/g, '')) || null;

// Sheets displays dates as M/D/YYYY; the app stores YYYY-MM-DD (what <input type="date"> reads).
// Returns '' for blank, the ISO string, or null when the cell can't be read as a date.
function parseSheetDate(value) {
  const v = (value || '').trim();
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!m) return null;
  const [, mo, dy, yr] = m;
  if (+mo < 1 || +mo > 12 || +dy < 1 || +dy > 31) return null;
  return `${yr.length === 2 ? `20${yr}` : yr}-${mo.padStart(2, '0')}-${dy.padStart(2, '0')}`;
}

function resolveDoseDays(value) {
  if (!value) return [];
  return value.split(',').map(t => DAY_MAP[t.trim().toLowerCase()]).filter(Boolean);
}

function resolveOpenedAt(status, providedDate) {
  if (status === 'open' && !providedDate) return new Date().toISOString().slice(0, 10);
  return providedDate || '';
}

function buildStackItems(rows, existingCompounds, errors) {
  const nameToKey = {};
  for (const [key, c] of Object.entries(existingCompounds || {})) {
    if (c?.label) nameToKey[c.label.trim().toLowerCase()] = key;
  }
  const compoundsToCreate = [];
  let createdCount = 0;
  const existingCount = Object.keys(existingCompounds || {}).length;

  for (const { row, sheetRow } of rows) {
    if (!row['Full Name'] && !row['Short Name'] && !row['Delivery Type']) continue; // fully blank row, skip silently
    if (!row['Full Name']) { errors.push({ tab: 'Stack Items', row: sheetRow, reason: 'Missing Full Name' }); continue; }
    if (!row['Short Name']) { errors.push({ tab: 'Stack Items', row: sheetRow, reason: 'Missing Short Name' }); continue; }
    const deliveryType = resolveDeliveryType(row['Delivery Type']);
    if (!deliveryType) { errors.push({ tab: 'Stack Items', row: sheetRow, reason: `Unrecognized Delivery Type "${row['Delivery Type']}" — must be one of: Syringe, Compounded, Pen Injector, Auto-Injector, Pills` }); continue; }
    const goal = resolveGoal(row['Goal']);
    if (!goal) { errors.push({ tab: 'Stack Items', row: sheetRow, reason: `Unrecognized Goal "${row['Goal']}"` }); continue; }

    // Time of Day is optional and only meaningful for pill compounds -- blank is valid
    // (Anytime). A recognized value on a non-pill row is accepted, not an import error
    // (a user may just leave a stray value there), but simply not applied, matching
    // saveCmpd's own deliveryType === 'pill' gate (useStoredActions.js) that would drop
    // it anyway if it were ever entered through the edit form instead.
    const timeOfDayCell = (row['Time of Day'] || '').trim();
    let timeOfDay;
    if (timeOfDayCell) {
      const match = TIME_OF_DAY_BUCKETS.find(b => b.label.toLowerCase() === timeOfDayCell.toLowerCase());
      if (!match) { errors.push({ tab: 'Stack Items', row: sheetRow, reason: `Unrecognized Time of Day "${timeOfDayCell}" — must be one of: Morning, Afternoon, Evening, Bedtime, Anytime (or leave blank)` }); continue; }
      if (deliveryType === 'pill' && match.value) timeOfDay = match.value;
    }

    // Blend Ingredients / Default BAC are optional and only meaningful for injectables — on a
    // pen/pill row they're accepted but not applied, same as Time of Day on a non-pill row.
    let blend, bacMl;
    if (supportsBlend(deliveryType)) {
      const parsed = parseBlendText(row['Blend Ingredients']);
      if (parsed.error) { errors.push({ tab: 'Stack Items', row: sheetRow, reason: parsed.error }); continue; }
      blend = parsed.blend;
      if (blend) bacMl = parseFloat(row['Default BAC (mL)']) || undefined;
    }

    const nameKey = row['Full Name'].trim().toLowerCase();
    if (nameToKey[nameKey]) continue; // already exists (pre-existing or created earlier in this same import) — reused, not recreated

    const pal = PALETTE[(existingCount + createdCount) % PALETTE.length];
    const key = row['Full Name'].toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + uid().slice(0, 8);
    const compound = {
      label: sanitize(row['Full Name'], 100), shortLabel: sanitize(row['Short Name'], 50), goal,
      color: pal.color, dim: pal.dim, text: pal.text, protocols: {}, syringeML: 1.0,
      freq: resolveDoseDays(row['Dose Days']), notes: '', builtIn: false, deliveryType, glp: false,
      ...(timeOfDay ? { timeOfDay } : {}),
      ...(blend ? { blend } : {}), ...(bacMl ? { bacMl } : {}),
    };
    compoundsToCreate.push({ key, compound });
    nameToKey[nameKey] = key;
    createdCount++;
  }
  return { compoundsToCreate, nameToKey };
}

function buildInventory(rows, tabName, labelHeader, countHeader, doseHeader, pillsPerDoseHeader, nameToKey, existingCompounds, existingVials, compoundsToCreate, errors) {
  const vialsToCreate = [];
  const compoundByKey = { ...existingCompounds };
  for (const { key, compound } of compoundsToCreate) compoundByKey[key] = compound;
  const hasExistingVialForCompound = key => (existingVials?.[key] || []).some(v => v.status !== 'archived');
  const grantedFirstActive = new Set();

  for (const { row, sheetRow } of rows) {
    if (!row['Stack Item Name'] && !row[labelHeader]) continue; // fully blank row
    const nameKey = (row['Stack Item Name'] || '').trim().toLowerCase();
    if (!nameKey) { errors.push({ tab: tabName, row: sheetRow, reason: 'Missing Stack Item Name' }); continue; }
    const compoundKey = nameToKey[nameKey];
    if (!compoundKey) { errors.push({ tab: tabName, row: sheetRow, reason: `Stack Item Name "${row['Stack Item Name']}" not found in Stack Items tab` }); continue; }
    const compound = compoundByKey[compoundKey];
    const isCounted = ['pill', 'pen', 'autoinjector'].includes(compound.deliveryType);
    const tabIsCounted = tabName === 'Pill & Pen Bottles';
    if (isCounted !== tabIsCounted) {
      errors.push({ tab: tabName, row: sheetRow, reason: `Stack Item "${row['Stack Item Name']}" is a ${compound.deliveryType} compound, which does not belong in the ${tabName} tab` });
      continue;
    }
    if (!row[labelHeader]) { errors.push({ tab: tabName, row: sheetRow, reason: `Missing ${labelHeader}` }); continue; }
    if (!row['Pharmacy']) { errors.push({ tab: tabName, row: sheetRow, reason: 'Missing Pharmacy' }); continue; }
    const countValue = parseFloat(row[countHeader]);
    if (!countValue || countValue <= 0) { errors.push({ tab: tabName, row: sheetRow, reason: `Missing or invalid ${countHeader}` }); continue; }
    let doseValue;
    if (doseHeader) {
      doseValue = parseFloat(row[doseHeader]);
      if (!doseValue || doseValue <= 0) { errors.push({ tab: tabName, row: sheetRow, reason: `Missing or invalid ${doseHeader}` }); continue; }
    }
    // Pills per Dose is optional and only meaningful for pill (not pen/autoinjector)
    // rows in this shared tab -- blank is valid (defaults to 1 downstream, same as
    // never setting it through VialForm). A recognized value on a pen/autoinjector
    // row is accepted, not an import error, but simply not applied -- mirrors
    // buildStackItems' Time of Day handling above.
    let pillsPerDose;
    if (pillsPerDoseHeader) {
      const cell = (row[pillsPerDoseHeader] || '').trim();
      if (cell) {
        const parsed = parseFloat(cell);
        if (!parsed || parsed <= 0) { errors.push({ tab: tabName, row: sheetRow, reason: `Invalid ${pillsPerDoseHeader} "${cell}" — must be a positive number (or leave blank)` }); continue; }
        if (compound.deliveryType === 'pill') pillsPerDose = parsed;
      }
    }
    const status = resolveStatus(row['Status']);
    if (!status) { errors.push({ tab: tabName, row: sheetRow, reason: `Unrecognized Status "${row['Status']}" — use New, Open or Empty` }); continue; }
    const receivedDate = parseSheetDate(row['Received Date']);
    if (receivedDate === null) { errors.push({ tab: tabName, row: sheetRow, reason: `Unreadable Received Date "${row['Received Date']}" — use MM/DD/YYYY or YYYY-MM-DD` }); continue; }
    const bud = parseSheetDate(row['BUD / Expiration']);
    if (bud === null) { errors.push({ tab: tabName, row: sheetRow, reason: `Unreadable BUD / Expiration "${row['BUD / Expiration']}" — use MM/DD/YYYY or YYYY-MM-DD` }); continue; }

    // Blend vial: the row's own recipe, else a copy of the stack item's default (like VialForm's
    // prefill). The recipe then defines conc — a typed Concentration is ignored, so an "80 mg
    // total" label can't be imported as 80 mg/mL.
    let blend;
    if (!tabIsCounted && supportsBlend(compound.deliveryType)) {
      const parsed = parseBlendText(row['Blend Ingredients']);
      if (parsed.error) { errors.push({ tab: tabName, row: sheetRow, reason: parsed.error }); continue; }
      blend = parsed.blend || (isBlend(compound.blend) ? compound.blend.map(i => ({ ...i })) : undefined);
    }

    // The template has no separate "Opened Date" column (only Received Date, which is a
    // different concept) — so an imported row marked Open always auto-defaults openedAt to
    // today, the same rule useVialActions.js already applies when a vial is saved as open
    // with no explicit opened date.
    const openedAt = resolveOpenedAt(status, '');
    const alreadyHasVial = hasExistingVialForCompound(compoundKey) || grantedFirstActive.has(compoundKey);
    const active = !alreadyHasVial;
    if (active) grantedFirstActive.add(compoundKey);

    const vial = {
      id: uid(), compound: compoundKey, label: sanitize(row[labelHeader], 200),
      pharmacy: sanitize(row['Pharmacy'], 200),
      ...(tabIsCounted ? { totalDoses: countValue, dosePerInjection: doseValue } : { conc: blend ? blendConc(blend, countValue) : (parseFloat(row['Concentration (mg/mL)']) || 0), totalVol: countValue }),
      ...(blend ? { blend } : {}),
      ...(pillsPerDose ? { pillsPerDose } : {}),
      status, openedAt, bud, receivedDate,
      lot: sanitize(row['Lot #'] || '', 100), notes: '', active,
      costPerVial: parseMoney(row['Cost ($)']),
    };
    vialsToCreate.push({ compoundKey, vial });
  }
  return vialsToCreate;
}

export function buildImportPlan({ stackItemsCsv, injectableVialsCsv, pillBottlesCsv, existingCompounds, existingVials }) {
  if (!stackItemsCsv) throw new Error('Stack Items CSV is required');
  const errors = [];

  const stackItemRows = parseWithHeaders(stackItemsCsv, STACK_ITEMS_HEADERS);
  const { compoundsToCreate, nameToKey } = buildStackItems(stackItemRows, existingCompounds, errors);

  const vialsToCreate = [];
  if (injectableVialsCsv) {
    const rows = parseWithHeaders(injectableVialsCsv, INJECTABLE_VIALS_HEADERS);
    vialsToCreate.push(...buildInventory(rows, 'Injectable Vials', 'Vial Label', 'Total Volume (mL)', null, null, nameToKey, existingCompounds, existingVials, compoundsToCreate, errors));
  }
  if (pillBottlesCsv) {
    const rows = parseWithHeaders(pillBottlesCsv, PILL_BOTTLES_HEADERS);
    vialsToCreate.push(...buildInventory(rows, 'Pill & Pen Bottles', 'Bottle/Pen Label', 'Pill/Dose Count', 'Dose per Pill/Pen (mg)', 'Pills per Dose', nameToKey, existingCompounds, existingVials, compoundsToCreate, errors));
  }

  return {
    compoundsToCreate, vialsToCreate, errors,
    summary: { compounds: compoundsToCreate.length, vials: vialsToCreate.length },
  };
}
